import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function NewsPage() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');

  useEffect(() => {
    async function fetchNews() {
      try {
        const { data } = await axios.get(`${API}/api/news`);
        setNews(data);
      } catch (error) {
        console.error('Failed to fetch news:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchNews();
  }, []);

  const categories = ['All', ...new Set(news.map(n => n.category))];
  const filteredNews = filter === 'All' ? news : news.filter(n => n.category === filter);

  return (
    <div className="bg-white pt-32 pb-24">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-3xl"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-[#0A192F]/50 mb-4">News & Press</p>
          <h1 className="font-serif text-4xl lg:text-5xl text-[#0A192F] tracking-tight mb-6">
            Latest Updates
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed">
            Stay informed about the latest developments at BNDV Capital and 
            our portfolio companies through Dolchester Holding.
          </p>
        </motion.div>
      </div>

      {/* Filter */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-12">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="flex flex-wrap gap-3"
        >
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-4 py-2 text-sm uppercase tracking-wider transition-all ${
                filter === cat
                  ? 'bg-[#0A192F] text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {cat}
            </button>
          ))}
        </motion.div>
      </div>

      {/* Featured Article */}
      {!loading && filteredNews.length > 0 && (
        <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-16">
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid lg:grid-cols-2 gap-8 bg-[#F8F9FA] overflow-hidden"
          >
            {filteredNews[0].image_url && (
              <Link to={`/news/${filteredNews[0].article_id}`} className="group">
                <div className="h-full min-h-[300px] lg:min-h-[400px] overflow-hidden">
                  <img
                    src={filteredNews[0].image_url}
                    alt={filteredNews[0].title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
              </Link>
            )}
            <div className="p-8 lg:p-12 flex flex-col justify-center">
              <div className="flex items-center gap-3 text-xs uppercase tracking-wider text-[#0A192F]/50 mb-4">
                <span className="bg-[#0A192F] text-white px-2 py-1">Featured</span>
                <span>{filteredNews[0].category}</span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {filteredNews[0].date}
                </span>
              </div>
              <Link to={`/news/${filteredNews[0].article_id}`} className="group">
                <h2 className="font-serif text-2xl lg:text-3xl text-[#0A192F] mb-4 group-hover:text-[#0A192F]/70 transition-colors leading-tight">
                  {filteredNews[0].title}
                </h2>
              </Link>
              <p className="text-slate-600 leading-relaxed mb-6">
                {filteredNews[0].excerpt}
              </p>
              <Link
                to={`/news/${filteredNews[0].article_id}`}
                className="inline-flex items-center gap-2 text-[#0A192F] text-sm uppercase tracking-wider font-medium hover:gap-4 transition-all"
              >
                Read Article
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.article>
        </div>
      )}

      {/* News Grid */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-48 bg-slate-200 mb-4" />
                <div className="h-4 bg-slate-200 w-1/3 mb-3" />
                <div className="h-6 bg-slate-200 w-full mb-2" />
                <div className="h-4 bg-slate-200 w-2/3" />
              </div>
            ))}
          </div>
        ) : filteredNews.length <= 1 ? (
          <p className="text-slate-600 text-center py-16">No additional articles in this category.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredNews.slice(1).map((article, index) => (
              <motion.article
                key={article.article_id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group"
                data-testid={`news-article-${article.article_id}`}
              >
                <Link to={`/news/${article.article_id}`}>
                  {article.image_url && (
                    <div className="overflow-hidden mb-4">
                      <img
                        src={article.image_url}
                        alt={article.title}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="flex items-center gap-3 text-xs uppercase tracking-wider text-[#0A192F]/50 mb-3">
                    <span>{article.category}</span>
                    <span>·</span>
                    <span>{article.date}</span>
                  </div>
                  <h3 className="font-serif text-xl text-[#0A192F] mb-3 group-hover:text-[#0A192F]/70 transition-colors leading-tight">
                    {article.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed line-clamp-2">
                    {article.excerpt}
                  </p>
                </Link>
              </motion.article>
            ))}
          </div>
        )}
      </div>

      {/* Press Contact CTA */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 mt-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-[#0A192F] p-12 text-center"
        >
          <h2 className="font-serif text-2xl text-white mb-4">Media Inquiries</h2>
          <p className="text-white/70 mb-6 max-w-xl mx-auto">
            For press inquiries, interview requests, or media information, please contact our communications team.
          </p>
          <a
            href="mailto:press@bndvcapital.com"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#0A192F] text-sm uppercase tracking-wider font-medium hover:bg-white/90 transition-colors"
          >
            press@bndvcapital.com
          </a>
        </motion.div>
      </div>
    </div>
  );
}
